/*
 * Copyright (c) 2023. American Express Company
 */

// third party
import Dialog from '@mui/material/Dialog';
import Popover, { PopoverOrigin } from '@mui/material/Popover';
import React from 'react';

// models and services
import MentorPopoverViewModel from 'src/types/view-models/mentor-popover.view.model';

// components
import MentorButtons from 'src/components/mentors/MentorButtons';
import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';

// styles
import styles from './Mentor.module.scss';
import { JaegerService } from 'src/services/jaeger.service';
import Selectors from 'src/utils/selectors';

export default function MentorPopover({
    className,
    dangerous,
    mentorX,
    mentorY,
    mentorHeight,
    position,
    body,
    buttonOneText,
    buttonOneHandler,
    buttonTwoText,
    buttonTwoHandler,
    learnMoreUrl,
    isOpen,
    onKeyUp,
    anchor
}: MentorPopoverViewModel): JSX.Element {
    let anchorOrigin: PopoverOrigin;
    let transformOrigin: PopoverOrigin;
    let arrow: JSX.Element = null;
    let mentorClass: string = className;

    let x: number = dangerous ? mentorX + JaegerService.getJaegerLeft() : mentorX;
    let y: number = mentorY;
    const popoverWidth = 450;
    const popoverHeight = 100;
    const height: number = mentorHeight || popoverHeight;

    switch (position) {
        case 'right':
            anchorOrigin = {
                vertical: 'center',
                horizontal: 'right'
            };
            transformOrigin = {
                vertical: 'center',
                horizontal: 'left'
            };
            if (mentorX) {
                y -= height;
                x += 8;
            }
            arrow = <div className={styles.arrowLeft} />;
            mentorClass = `${mentorClass} ${styles.mentorRight}`;
            break;

        case 'left':
            anchorOrigin = {
                vertical: 'center',
                horizontal: 'left'
            };
            transformOrigin = {
                vertical: 'center',
                horizontal: 'right'
            };
            if (mentorX) {
                x -= popoverWidth - 8;
                y -= height / 2;
            }
            arrow = <div className={styles.arrowRight} />;
            mentorClass = `${mentorClass} ${styles.mentorLeft}`;
            break;

        case 'top':
            anchorOrigin = {
                vertical: 'top',
                horizontal: 'center'
            };
            transformOrigin = {
                vertical: 'bottom',
                horizontal: 'center'
            };
            if (mentorX) {
                x -= popoverWidth / 2;
                y -= height - 8;
            }
            arrow = <div className={styles.arrowBottom} />;
            mentorClass = `${mentorClass} ${styles.mentorTop}`;
            break;

        case 'top-left':
            anchorOrigin = {
                vertical: 'top',
                horizontal: 'right'
            };
            transformOrigin = {
                vertical: 'bottom',
                horizontal: 'right'
            };
            if (mentorX) {
                x -= popoverWidth + 30;
                y -= height - 8;
            }
            arrow = <div className={styles.arrowBottomRight} />;
            mentorClass = `${mentorClass} ${styles.mentorTop}`;
            break;

        case 'bottom':
            anchorOrigin = {
                vertical: 'bottom',
                horizontal: 'center'
            };
            transformOrigin = {
                vertical: 'top',
                horizontal: 'center'
            };
            if (mentorX) {
                x -= popoverWidth / 2;
                y += 8;
            }
            arrow = <div className={styles.arrowTop} />;
            mentorClass = `${mentorClass} ${styles.mentorBottom}`;
            break;

        case 'bottom-right':
            anchorOrigin = {
                vertical: 'bottom',
                horizontal: 'center'
            };
            transformOrigin = {
                vertical: 'top',
                horizontal: 'center'
            };
            if (mentorX) {
                x -= popoverWidth + 30;
                y += 8;
            }
            arrow = <div className={styles.arrowTopLeft} />;
            break;

        case 'bottom-left':
            anchorOrigin = {
                vertical: 'bottom',
                horizontal: 'center'
            };
            transformOrigin = {
                vertical: 'top',
                horizontal: 'center'
            };
            if (mentorX) {
                x -= popoverWidth + 30;
                y += 8;
            }
            arrow = <div className={styles.arrowTopRight} />;
            mentorClass = `${mentorClass} ${styles.mentorBottomLeft}`;
            break;
    }

    const mentorStyles = mentorX
        ? {
              left: x,
              right: 'unset',
              top: y,
              bottom: 'unset'
          }
        : {};

    const interior: JSX.Element = (
        <div>
            <div className={styles.body}>
                <Typography variant={TypographyVariants.SUBTEXT} white>
                    {body}
                </Typography>
                {learnMoreUrl && (
                    <Typography variant={TypographyVariants.SUBTEXT} white>
                        <a target="_blank" rel="noopener noreferrer" href={learnMoreUrl}>
                            Learn more
                        </a>
                    </Typography>
                )}
                <MentorButtons
                    buttonOneText={buttonOneText}
                    buttonOneHandler={buttonOneHandler}
                    buttonTwoText={buttonTwoText}
                    buttonTwoHandler={buttonTwoHandler}
                />
            </div>
            {arrow}
        </div>
    );

    return mentorX ? (
        <Dialog
            open={isOpen}
            PaperProps={{ className: 'paper' }}
            className={`${styles.mentorPopover} ${styles.popoverShownBlock} ${mentorClass}`}
            onKeyUp={(e: any) => {
                void onKeyUp(e, buttonOneHandler);
            }}
            style={mentorStyles}
            data-cy={Selectors.General.MENTOR}
        >
            {interior}
        </Dialog>
    ) : (
        <Popover
            anchorOrigin={anchorOrigin}
            transformOrigin={transformOrigin}
            open={isOpen}
            PaperProps={{ className: styles.paper }}
            className={`${styles.mentorPopover} ${styles.popoverShownBlock} ${mentorClass}`}
            anchorEl={anchor}
            onKeyUp={(e: any) => {
                void onKeyUp(e, buttonOneHandler);
            }}
            data-cy={Selectors.General.MENTOR}
        >
            {interior}
        </Popover>
    );
}
